import { AfterViewInit, Directive, ElementRef, OnDestroy } from '@angular/core';
import { WindowService } from '../../services/popr-service/window.service';
import { fromEvent, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[wproTrapFocus]',
  standalone: false
})
export class TrapFocusDirective implements AfterViewInit, OnDestroy {

  constructor(
    private windowService: WindowService,
    private elementRef: ElementRef,
  ) { }

  private keyInput = fromEvent<KeyboardEvent>(this.windowService.window.document, 'keydown');

  private destroy$: Subject<void> = new Subject<void>();

  static setFocusOnLastElement(
    currentFocusIsNotOnElementInsideFocusTrapArea: boolean,
    shifKeyIsPressed: boolean,
    activeElement: Element | null,
    firstFocusableElement: Element,
  ): any {
    return (
      (currentFocusIsNotOnElementInsideFocusTrapArea && shifKeyIsPressed) ||
      (activeElement === firstFocusableElement && shifKeyIsPressed)
    );
  }

  static setFocusOnFirstElement(
    currentFocusIsNotOnElementInsideFocusTrapArea: boolean,
    shifKeyIsPressed: boolean,
    activeElement: Element | null,
    lastFocusableElement: Element,
  ): any {
    return (
      (currentFocusIsNotOnElementInsideFocusTrapArea && !shifKeyIsPressed) ||
      (activeElement === lastFocusableElement && !shifKeyIsPressed)
    );
  }

  static isVisibleInTheDOM(el: HTMLElement): boolean {
    return Boolean(el.offsetParent);
  }

  ngAfterViewInit(): void {
    this.trapFocus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private trapFocus() {
    this.keyInput
      .pipe(
        filter((event: KeyboardEvent) => event.key === 'Tab'),
        takeUntil(this.destroy$),
      ).subscribe(this.onTab.bind(this));
  }

  private onTab(event: KeyboardEvent) {
    const focusableElements = this.getFocusableElements();
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    const activeElement = this.windowService.window.document.activeElement as HTMLElement;
    const currentFocusIsNotOnElementInsideFocusTrapArea = !focusableElements.includes(activeElement);
    const shifKeyIsPressed = event.shiftKey;

    if (
      TrapFocusDirective.setFocusOnLastElement(
        currentFocusIsNotOnElementInsideFocusTrapArea,
        shifKeyIsPressed,
        activeElement,
        firstFocusableElement,
      )
    ) {
      lastFocusableElement.focus();
      event.preventDefault();
      return;
    }

    if (
      TrapFocusDirective.setFocusOnFirstElement(
        currentFocusIsNotOnElementInsideFocusTrapArea,
        shifKeyIsPressed,
        activeElement,
        lastFocusableElement,
      )
    ) {
      firstFocusableElement.focus();
      event.preventDefault();
      return;
    }
  }

  private getFocusableElements(): HTMLElement[] {
    let focusableElements: HTMLElement[] = Array.from(this.elementRef.nativeElement.querySelectorAll('button, [href]:not(use), input, select, textarea, [tabindex]'));
    focusableElements = focusableElements.filter(TrapFocusDirective.isVisibleInTheDOM);
    return focusableElements;
  }

}
